import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, TicketPercent, LogOut } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

const HIGHLIGHTS = {
  starter: [
    "Up to 5 CRM users",
    "Weighted quarterly forecast",
    "Risk Radar + Funnel X-Ray",
    "Weekly AI briefing",
  ],
  growth: [
    "Up to 15 CRM users",
    "Everything in Starter",
    "Team performance & quotas",
    "Priority sync",
  ],
  scale: [
    "Up to 25 CRM users",
    "Everything in Growth",
    "Custom onboarding",
    "Dedicated CS contact",
  ],
};

const PLAN_ORDER = ["starter", "growth", "scale"];

function groupByPlan(plans) {
  const bySlug = {};
  for (const p of plans) {
    bySlug[p.plan_slug] = bySlug[p.plan_slug] || { slug: p.plan_slug, label: p.label, seats_max: p.seats_max };
    bySlug[p.plan_slug][p.interval] = p; // "month" or "year"
  }
  return PLAN_ORDER.map((s) => bySlug[s]).filter(Boolean);
}

export default function PaywallPage() {
  const { billing, logout } = useAuth();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null); // lookup_key currently in flight
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/billing/plans")
      .then(({ data }) => setPlans(groupByPlan(data.plans || [])))
      .catch(() => setPlans([]));
  }, []);

  const startCheckout = async (lookup_key) => {
    setBusy(lookup_key);
    setErr("");
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const grouped = plans || [];
  const daysLeft = billing?.trial_days_left;
  const heroTitle =
    billing?.state === "trialing"
      ? `Your trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`
      : "Your 14-day trial has ended";
  const heroSub =
    billing?.state === "trialing"
      ? "Pick a plan now to keep uninterrupted access — your card is only charged after the trial ends."
      : "Pick a plan to unlock the dashboard. Nightly syncs still ran during a 14-day grace period — nothing was lost.";

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 to-slate-900 text-white" data-testid="paywall-page">
      <header className="mx-auto max-w-6xl px-6 lg:px-10 pt-8 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RecrystaMark size={36} />
          <div>
            <div className="text-sm font-bold tracking-tight">Recrysta</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">
              {billing?.state === "trialing" ? "Trial · locking soon" : "Locked · pick a plan"}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5"
          data-testid="paywall-signout"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </header>

      <div className="mx-auto max-w-4xl px-6 lg:px-10 pt-6 pb-4 text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="paywall-title">
          {heroTitle}
        </h1>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-xl mx-auto">{heroSub}</p>

        <div className="mt-8 inline-flex items-center rounded-full bg-slate-800/70 p-1" data-testid="interval-toggle">
          <button
            onClick={() => setInterval("month")}
            data-testid="interval-monthly"
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
              interval === "month" ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval("year")}
            data-testid="interval-yearly"
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition flex items-center gap-1.5 ${
              interval === "year" ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"
            }`}
          >
            Annual
            <span className="text-[10px] uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
              2 months free
            </span>
          </button>
        </div>
      </div>

      {err && (
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="mt-4 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="paywall-error">
            {err}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-6 lg:px-10 pt-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {grouped.map((p, idx) => {
            const price = p[interval];
            if (!price) return null;
            const isFeatured = p.slug === "growth";
            const highlights = HIGHLIGHTS[p.slug] || [];
            return (
              <div
                key={p.slug}
                data-testid={`plan-card-${p.slug}`}
                className={`rounded-2xl p-6 flex flex-col relative ${
                  isFeatured
                    ? "bg-white text-slate-900 shadow-2xl ring-2 ring-emerald-400/60 -mt-3"
                    : "bg-slate-900/60 border border-slate-700/70 text-white"
                }`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1">
                    Most popular
                  </div>
                )}
                <div className={`text-xs uppercase tracking-[0.18em] font-semibold ${isFeatured ? "text-emerald-700" : "text-slate-400"}`}>
                  {p.label}
                </div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <div className="text-4xl font-extrabold tracking-tight" data-testid={`plan-price-${p.slug}`}>
                    €{price.amount_eur.toLocaleString()}
                  </div>
                  <div className={`text-sm ${isFeatured ? "text-slate-500" : "text-slate-400"}`}>
                    /{interval === "year" ? "yr" : "mo"}
                  </div>
                </div>
                <div className={`mt-1 text-xs ${isFeatured ? "text-slate-500" : "text-slate-400"}`}>
                  {interval === "year" ? `€${Math.round(price.amount_eur / 12).toLocaleString()} per month, billed yearly` : "billed monthly"}
                </div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {highlights.map((h) => (
                    <li key={h} className={`flex items-start gap-2 text-sm ${isFeatured ? "text-slate-700" : "text-slate-300"}`}>
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isFeatured ? "text-emerald-600" : "text-emerald-400"}`} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startCheckout(price.lookup_key)}
                  disabled={busy === price.lookup_key}
                  data-testid={`plan-cta-${p.slug}`}
                  className={`mt-6 w-full rounded-lg py-2.5 text-sm font-semibold transition ${
                    isFeatured
                      ? "bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400"
                      : "bg-white text-slate-900 hover:bg-slate-200 disabled:opacity-50"
                  }`}
                >
                  {busy === price.lookup_key ? "Redirecting…" : `Choose ${p.label}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs text-slate-400 flex items-center justify-center gap-2" data-testid="paywall-footer-hints">
          <TicketPercent className="h-4 w-4" />
          Have a coupon code? Enter it during Checkout. VAT is added automatically at checkout.
        </div>
      </div>
    </div>
  );
}
